import { useEffect, useMemo, useRef, useState } from 'react'
import useSWR, { mutate as mutateGlobal } from 'swr'
import { bscTokens } from '@pancakeswap/tokens'
import { cadinuProfileAbi } from 'config/abi/cadinuProfile'
import { cadinuReferralAbi } from 'config/abi/cadinuReferral'
import { CadinuLevelNftsAbi } from 'config/abi/cadinuLevelNfts'
import { getCadinuProfileAddress, getCadinuReferralAddress } from 'utils/addressHelpers'
import { publicClient } from 'utils/wagmi'
import { formatUnits } from 'viem'
import { PROFILE_CHAIN_ID, txLabel, useChainTx } from './chainTx'
import { ActionBtn, Hint, Note } from './Identity'
import MyReferralLink from './MyReferralLink'
import { ERC20_ABI, collectionInfo } from './onchainReads'
import { buyTotal, isAddr, parseCount, referralTerms } from './onchainMath'
import { Check, PickTile, Segmented, Step, Steps, Sum, TextInput, TxStatus, Warn } from './onchainUi'
import { inviterLabel } from './referral'
import { useDefaultReferrer } from './useReferral'

const CBON = bscTokens.cbon.address as `0x${string}`
const client = () => publicClient({ chainId: PROFILE_CHAIN_ID })
const cbon = (wei?: bigint) => (wei === undefined ? '…' : Number(formatUnits(wei, 18)).toLocaleString('en-US', { maximumFractionDigits: 2 }))
const SOURCE_TEXT = { bound: 'the friend who invited you (bound at your first sign-in)', link: 'your invite link (?ref=)', invite: 'the invite link you opened (/r/)' } as const

interface Collection {
  address: `0x${string}`
  character: string
  image: string | null
  level: number
  price: bigint
  discountBp: bigint
  left: bigint
}

/** The level's collections with price, referral discount and remaining supply, all read on BNB Chain (nothing written in the code). */
function useLevelCollections(level: number) {
  return useSWR<Collection[]>(['cadinu-buy-collections', level], async () => {
    const cols = (await client().readContract({ address: getCadinuProfileAddress(), abi: cadinuProfileAbi, functionName: 'getNftAddressesForLevel', args: [BigInt(level)] })) as readonly `0x${string}`[]
    const reads = await client().multicall({
      allowFailure: true,
      contracts: cols.flatMap((c) => [
        { address: c, abi: CadinuLevelNftsAbi, functionName: 'priceInCbon' },
        { address: c, abi: CadinuLevelNftsAbi, functionName: 'referralDiscount' },
        { address: c, abi: CadinuLevelNftsAbi, functionName: 'totalSupply' },
        { address: c, abi: CadinuLevelNftsAbi, functionName: 'maxSupply' },
      ]) as any,
    })
    return cols
      .map((c, i) => {
        const v = (k: number) => reads[i * 4 + k]?.result as bigint | undefined
        const info = collectionInfo(c)
        const supply = v(2)
        const max = v(3)
        return { address: c, character: info.character, image: info.image, level, price: v(0) ?? 0n, discountBp: v(1) ?? 0n, left: supply !== undefined && max !== undefined ? max - supply : 0n }
      })
      .filter((c) => c.price > 0n)
  }, { revalidateOnFocus: false })
}

/**
 * Buy CADINU Identity Arts NFTs from the profile (Bez, 2026-10-03: merge /buy-nft into /u). Same calls as the legacy page:
 * CBON approve to the NFT contract, then buyNFTs(to, count, referral). Prices, discount and supply are read on chain; the
 * approval is the exact total the contract pulls (the legacy page approved one price, so a multi-buy reverted).
 */
export default function BuyNftSheet({ account, refQuery, onBought }: { account?: `0x${string}`; refQuery?: unknown; onBought?: () => void }) {
  const { data: nLevels } = useSWR(['cadinu-levels'], () => client().readContract({ address: getCadinuProfileAddress(), abi: cadinuProfileAbi, functionName: 'numberOfLevels' }), { revalidateOnFocus: false })
  const levels = Array.from({ length: Number(nLevels ?? 2n) }, (_, i) => i + 1)
  const [level, setLevel] = useState(1)
  const { data: cols } = useLevelCollections(level)
  const [selAddr, setSelAddr] = useState<`0x${string}` | null>(null)
  const sel = cols?.find((c) => c.address === selAddr) ?? null
  const [countText, setCountText] = useState('1')
  const [forOther, setForOther] = useState(false)
  const [recipient, setRecipient] = useState('')
  const [useRef_, setUseRef] = useState(false)
  const [referrer, setReferrer] = useState('')
  const approve = useChainTx()
  const buy = useChainTx()

  const { data: wallet, mutate: refreshWallet } = useSWR(
    account ? ['cadinu-buy-wallet', account, selAddr] : null,
    async () => {
      const r = await client().multicall({
        allowFailure: true,
        contracts: [
          { address: getCadinuReferralAddress(), abi: cadinuReferralAbi, functionName: 'referredTo', args: [account as `0x${string}`] },
          { address: CBON, abi: ERC20_ABI, functionName: 'balanceOf', args: [account as `0x${string}`] },
          ...(selAddr ? [{ address: CBON, abi: ERC20_ABI, functionName: 'allowance', args: [account as `0x${string}`, selAddr] }] : []),
        ] as any,
      })
      return { referredTo: r[0]?.result as string | undefined, balance: r[1]?.result as bigint | undefined, allowance: selAddr ? (r[2]?.result as bigint | undefined) : undefined }
    },
    { revalidateOnFocus: false },
  )

  // the referrer prefill (legacy BuyNft): bound inviter > ?ref= > /r/ invite; applied once, still editable and clearable
  const defRef = useDefaultReferrer({ account, recipient: forOther ? recipient : undefined, query: refQuery })
  const applied = useRef(false)
  useEffect(() => {
    if (applied.current || !defRef.ready || !wallet) return
    applied.current = true
    if (isAddr(wallet.referredTo)) return
    if (defRef.referrer) {
      setReferrer(defRef.referrer)
      setUseRef(true)
    }
  }, [defRef.ready, defRef.referrer, wallet])

  const referred = isAddr(wallet?.referredTo)
  const terms = referralTerms({ referredTo: wallet?.referredTo, useReferrer: useRef_, referrer, payer: account, recipient: forOther ? recipient : undefined })
  const count = parseCount(countText, sel ? sel.left : undefined)
  const cost = useMemo(() => (sel && count ? buyTotal(sel.price, count, sel.discountBp, terms.source !== null) : null), [sel, count, terms.source])
  const to = forOther ? recipient.trim() : account
  const badRecipient = forOther && !isAddr(recipient.trim())
  const enoughCbon = cost && wallet?.balance !== undefined ? wallet.balance >= cost.total : undefined
  const busy = approve.busy || buy.busy
  const done = buy.state === 'success'
  // once bought, the allowance is spent: the steps stay as they ended
  const approved = done || (cost && wallet?.allowance !== undefined ? wallet.allowance >= cost.total : undefined)
  const formOk = Boolean(sel && count && cost && !badRecipient && !terms.problem)
  const refSourceText = terms.source === 'referred' ? 'you are already referred on chain' : terms.source === 'referrer' ? (defRef.source && defRef.referrer?.toLowerCase() === referrer.toLowerCase() ? `referrer from ${SOURCE_TEXT[defRef.source]}` : 'the referrer you entered') : null

  const resetTx = () => {
    approve.reset()
    if (!done) buy.reset()
  }

  return (
    <div style={{ display: 'grid', gap: 18 }} data-testid="buy-sheet">
      <Hint label="CADINU NFTs" text="CADINU Identity Arts NFTs on BNB Chain, paid in CBON. Each one adds profile points, and one locked in your on-chain profile becomes your on-chain picture. With a referrer you pay 10% less.">
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>Choose a character</h3>
      </Hint>
      {levels.length > 1 && (
        <Segmented role="group" aria-label="Level">
          {levels.map((l) => (
            <button
              key={l}
              type="button"
              aria-pressed={level === l}
              disabled={busy}
              onClick={() => {
                setLevel(l)
                setSelAddr(null)
                resetTx()
              }}
              data-testid={`buy-level-${l}`}
            >
              Level {l}
            </button>
          ))}
        </Segmented>
      )}
      {!cols ? (
        <Note aria-busy="true">Reading prices on BNB Chain…</Note>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8 }} data-testid="buy-grid">
          {cols.map((c) => (
            <PickTile
              key={c.address}
              type="button"
              $lvl={c.level}
              aria-pressed={selAddr === c.address}
              disabled={busy || c.left <= 0n}
              onClick={() => {
                setSelAddr(c.address)
                resetTx()
              }}
              data-testid={`buy-pick-${c.character}-${c.level}`}
              aria-label={`${c.character}, level ${c.level}, ${cbon(c.price)} CBON, ${c.left.toString()} left`}
            >
              {c.image ? <img src={c.image} alt="" loading="lazy" /> : <span style={{ width: '100%', aspectRatio: '1' }} />}
              <span className="lv">L{c.level}</span>
              <span className="nm">{c.character}</span>
              <span className="sub">{Number(formatUnits(c.price, 18)) >= 1e6 ? `${Number(formatUnits(c.price, 24)).toLocaleString('en-US')}M` : cbon(c.price)} CBON</span>
            </PickTile>
          ))}
        </div>
      )}

      <div style={{ display: 'grid', gap: 6 }}>
        <span id="buy-count-label" style={{ fontSize: 15, fontWeight: 700 }}>
          How many
        </span>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <ActionBtn type="button" aria-label="One fewer" disabled={busy || !count || count <= 1n} onClick={() => setCountText(String((count ?? 2n) - 1n))} style={{ padding: 0, width: 44 }}>
            −
          </ActionBtn>
          <TextInput id="buy-count" aria-labelledby="buy-count-label" inputMode="numeric" value={countText} disabled={busy} onChange={(e) => { setCountText(e.target.value); resetTx() }} style={{ width: 80, textAlign: 'center', fontFamily: 'inherit' }} data-testid="buy-count" />
          <ActionBtn type="button" aria-label="One more" disabled={busy || !count || (sel ? count >= sel.left : false)} onClick={() => setCountText(String((count ?? 0n) + 1n))} style={{ padding: 0, width: 44 }}>
            +
          </ActionBtn>
          {sel && <Note>{sel.left.toLocaleString()} left</Note>}
        </div>
        {!count && <Note role="alert" style={{ color: '#ff9b9b' }}>Enter a whole number from 1{sel ? ` to ${sel.left.toString()}` : ''}.</Note>}
      </div>

      <div style={{ display: 'grid', gap: 6 }} data-testid="buy-referral">
        {referred ? (
          <>
            <strong style={{ fontSize: 15 }}>Referral: 10% off</strong>
            <TextInput id="refAddress" data-testid="nft-ref-input" value={wallet?.referredTo ?? ''} disabled aria-label="Referrer (bound on chain)" />
            <Note data-testid="nft-ref-bound">This wallet is already referred on chain, so every purchase it pays for is 10% off. The referrer cannot change.</Note>
          </>
        ) : (
          <>
            <Check htmlFor="haveRef">
              <input id="haveRef" type="checkbox" checked={useRef_} disabled={busy} onChange={(e) => { setUseRef(e.target.checked); resetTx() }} />I have a referrer (10% off)
            </Check>
            {useRef_ && (
              <>
                <TextInput id="refAddress" data-testid="nft-ref-input" aria-label="Referrer address" placeholder="Referrer 0x…" value={referrer} disabled={busy} onChange={(e) => { setReferrer(e.target.value.trim()); resetTx() }} />
                {terms.problem === 'self' && <Note role="alert" style={{ color: '#ff9b9b' }} data-testid="nft-ref-self">You cannot refer yourself or the wallet you buy for.</Note>}
                {terms.problem === 'invalid' && <Note role="alert" style={{ color: '#ff9b9b' }}>That is not a wallet address.</Note>}
                {defRef.source === 'bound' && defRef.invitedBy && referrer.toLowerCase() === defRef.invitedBy.address.toLowerCase() && (
                  <Note data-testid="nft-ref-invited">{inviterLabel(defRef.invitedBy)}, bound to your wallet.</Note>
                )}
                <Note>The first referrer you buy with is bound to your wallet for good.</Note>
              </>
            )}
          </>
        )}
        <Check htmlFor="buyForOthers">
          <input id="buyForOthers" type="checkbox" checked={forOther} disabled={busy} onChange={(e) => { setForOther(e.target.checked); resetTx() }} />Buy for another wallet
        </Check>
        {forOther && (
          <>
            <TextInput aria-label="Recipient address" placeholder="Recipient 0x…" value={recipient} disabled={busy} onChange={(e) => { setRecipient(e.target.value); resetTx() }} data-testid="buy-recipient" />
            {recipient && badRecipient && <Note role="alert" style={{ color: '#ff9b9b' }}>That is not a wallet address.</Note>}
            <Note>You pay; the NFTs go to this wallet. The referral discount follows your wallet, not the recipient.</Note>
          </>
        )}
      </div>

      {sel && cost && count && (
        <div style={{ display: 'grid', gap: 8, padding: 14, borderRadius: 16, background: 'rgba(127,212,232,0.06)', border: '1px solid rgba(127,212,232,0.2)' }} data-testid="buy-summary">
          <Sum label={`${sel.character.charAt(0).toUpperCase()}${sel.character.slice(1)} (level ${sel.level}) × ${count.toString()}`} value={`${cbon(cost.gross)} CBON`} />
          {terms.source ? (
            <Sum label={`Referral −${Number(sel.discountBp) / 100}% (${refSourceText})`} value={`−${cbon(cost.discount)} CBON`} tone="ok" testId="buy-discount" />
          ) : (
            <Sum label="Referral discount" value="none" testId="buy-discount" />
          )}
          <Sum label="Total" value={`${cbon(cost.total)} CBON`} strong testId="buy-total" />
          <Sum label="Your CBON" value={`${cbon(wallet?.balance)} CBON`} tone={enoughCbon === false ? 'bad' : undefined} />
          <Note>Plus the network fee in BNB. Prices are read from the NFT contract.</Note>
        </div>
      )}
      {enoughCbon === false && cost && (
        <Warn data-testid="buy-short">
          Not enough CBON: {cbon(cost.total - (wallet?.balance ?? 0n))} more needed.{' '}
          <a href={`/swap?inputCurrency=0x55d398326f99059fF775485246999027B3197955&outputCurrency=${CBON}`} style={{ textDecoration: 'underline' }}>
            Get CBON
          </a>
        </Warn>
      )}

      <Steps>
        <Step n={1} title={cost ? `Approve ${cbon(cost.total)} CBON` : 'Approve CBON'} state={!formOk ? 'todo' : approved ? 'done' : 'now'} testId="step-approve">
          <Note>Lets the {sel ? sel.character : 'NFT'} contract take exactly this amount, once.</Note>
          {!approved && (
            <ActionBtn
              type="button"
              $primary
              disabled={!formOk || busy || done || enoughCbon === false || approved === undefined}
              onClick={async () => {
                if (!sel || !cost) return
                if (await approve.run({ address: CBON, abi: ERC20_ABI, functionName: 'approve', args: [sel.address, cost.total] })) await refreshWallet()
              }}
              style={{ justifySelf: 'start' }}
              data-testid="approve-cbon"
            >
              {txLabel(approve.state, 'Approve CBON', 'Approved')}
            </ActionBtn>
          )}
          <TxStatus state={approve.state} error={approve.error} hash={approve.hash} done="Approved." testId="approve-status" />
        </Step>
        <Step n={2} title={count && count > 1n ? `Buy ${count.toString()} NFTs` : 'Buy the NFT'} state={done ? 'done' : formOk && approved ? 'now' : 'todo'} testId="step-confirm">
          <ActionBtn
            type="button"
            $primary
            disabled={!formOk || !approved || busy || done || enoughCbon === false}
            onClick={async () => {
              if (!sel || !count || !to) return
              if (await buy.run({ address: sel.address, abi: CadinuLevelNftsAbi, functionName: 'buyNFTs', args: [to, count, terms.arg] })) {
                refreshWallet()
                if (account) mutateGlobal(`nfts:${account}`)
                mutateGlobal(['cadinu-buy-collections', level])
                onBought?.()
              }
            }}
            style={{ justifySelf: 'start' }}
            data-testid="confirm-buy"
          >
            {txLabel(buy.state, count && count > 1n ? `Buy ${count.toString()}` : 'Buy', 'Bought')}
          </ActionBtn>
          {!approved && formOk && !done && <Note>Approve the CBON first.</Note>}
          <TxStatus state={buy.state} error={buy.error} hash={buy.hash} done={`Bought ${count?.toString() ?? ''} × ${sel ? sel.character.charAt(0).toUpperCase() + sel.character.slice(1) : 'NFT'}. ${count && count > 1n ? 'They are' : 'It is'} in ${forOther ? 'the recipient’s' : 'your'} wallet.`} testId="buy-status" />
        </Step>
      </Steps>

      {account && (
        <div data-testid="nft-my-ref" style={{ paddingTop: 6, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <MyReferralLink wallet={account} title="Your referral link" />
        </div>
      )}
    </div>
  )
}
